// =============================================================================
// Pattern metadata — layperson names, technical names, citations, manifest refs.
//
// One entry per possible Classification.patternId (13 substantive + 2 meta +
// 1 severity override). Values mirror decision-logic.yaml. Update both when
// editing — spec-sync.test.ts guards the numeric threshold drift; this file
// covers the string + citation metadata.
// =============================================================================

import type { WinnerPatternId } from './arbitrate.js';
import type { EvidenceBadge } from './types.js';

export interface PatternMetadata {
  laypersonName: string;
  technicalName: string;
  strength: EvidenceBadge;
  primaryCitations: string[];
  supportingCitations: string[];
  manifestRefs: string[];
}

export const PATTERN_METADATA: Record<WinnerPatternId, PatternMetadata> = {
  stage_0_iron_replete: {
    laypersonName: 'Your iron levels look healthy',
    technicalName: 'Iron-replete (Stage 0)',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['addo_2025', 'galetti_2021'],
    supportingCitations: [],
    manifestRefs: ['A1'],
  },
  stage_1_incipient_id: {
    laypersonName: 'Early signs of iron running low',
    technicalName: 'Incipient iron deficiency (Stage 1)',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['galetti_2021', 'tarancon_diez_2022'],
    // Olsen 2010 documented iron deficiency in non-anemic women presenting
    // with hair-shedding / chronic telogen effluvium; surfaces here as a
    // supporting signal that incipient-stage iron status is clinically
    // meaningful before frank anemia.
    supportingCitations: ['olsen_2010'],
    manifestRefs: ['A1'],
  },
  stage_2_iron_depletion_no_anemia: {
    laypersonName: 'Iron stores running low, not yet anemic',
    technicalName: 'Iron depletion without anemia (Stage 2)',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['addo_2025', 'mei_2021'],
    // Hirosawa 2022 — retrospective cohort in general internal medicine
    // documenting the clinical + biological manifestations of iron
    // deficiency without anemia (IDWA) vs IDA. Anchors the "this stage
    // is genuinely symptomatic, not a pre-clinical artifact" framing.
    supportingCitations: ['olsen_2010', 'hirosawa_2022'],
    manifestRefs: ['A1'],
  },
  stage_3_iron_deficient_erythropoiesis: {
    laypersonName: 'Early iron shortage showing in red blood cells',
    technicalName: 'Iron-deficient erythropoiesis without anemia (Stage 3)',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['mei_2021', 'addo_2025', 'brownlie_2004'],
    // Hirosawa 2022 — supports the IDWA-vs-IDA continuum framing for
    // Stage 3 in particular, where clinical manifestations begin to
    // resemble IDA without the formal anemia threshold being crossed.
    supportingCitations: ['mei_2025', 'choorapoikayil_2025', 'olsen_2010', 'hirosawa_2022'],
    manifestRefs: ['A2'],
  },
  stage_4_iron_deficiency_anemia: {
    laypersonName: 'Iron deficiency anemia',
    technicalName: 'Iron deficiency anemia (IDA, Stage 4)',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['mei_2021', 'addo_2025', 'skikne_2011'],
    supportingCitations: [],
    manifestRefs: ['I1', 'A1', 'A2'],
  },
  inflammation_masks_iron_status: {
    laypersonName: 'Your ferritin cannot be read at face value right now',
    technicalName: 'Inflammation inflates ferritin — interpretation unreliable',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['namaste_2017', 'thurnham_2010', 'williams_2019', 'ko_2024'],
    // Young 2019 BRINDA pregnancy abstract — extends the BRINDA
    // inflammation-adjustment framework to pregnant women specifically,
    // where the acute-phase response baseline differs and sTfR (less
    // inflammation-sensitive than ferritin) plays a complementary role.
    supportingCitations: ['young_2019_brinda'],
    manifestRefs: ['D1'],
  },
  functional_iron_deficiency: {
    laypersonName: 'Iron in the body, but locked away',
    technicalName: 'Functional iron deficiency',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['kuragano_2020', 'rambod_2008', 'dignass_2018', 'skikne_2011'],
    supportingCitations: ['cacoub_2022', 'rohr_2023', 'svenson_2021', 'deniz_2022'],
    manifestRefs: ['I2'],
  },
  anemia_of_chronic_disease_classic: {
    laypersonName: 'Anemia that comes from long-term inflammation',
    technicalName: 'Anemia of chronic disease (ACD)',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['skikne_2011', 'rohr_2023', 'dignass_2018'],
    supportingCitations: ['svenson_2021', 'murawska_2016', 'auerbach_2025'],
    manifestRefs: ['I1', 'I2'],
  },
  iron_overload_trigger_premenopausal: {
    laypersonName: 'Your iron markers are above the evaluation threshold — time to see a clinician',
    technicalName: 'Ferritin + TSAT above premenopausal hemochromatosis evaluation threshold',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['easl_2022', 'adams_2005', 'allen_2008'],
    supportingCitations: ['lim_2014', 'schmitt_2005', 'cullis_2018', 'adams_2015'],
    manifestRefs: ['I3', 'A10'],
  },
  iron_overload_trigger_postmenopausal: {
    laypersonName: 'Your iron markers are above the evaluation threshold for your life stage — time to see a clinician',
    technicalName: 'Ferritin + TSAT above postmenopausal hemochromatosis evaluation threshold',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['easl_2022', 'adams_2005', 'warne_2017'],
    // Allen 2008 + Savatt 2023 anchor the postmenopausal-specific overload
    // expression: HFE C282Y/C282Y penetrance is dramatically higher post-
    // menopause once menstrual iron loss stops buffering. Allen 2008
    // documented the 28.4% (men) vs 1.2% (women) midlife
    // iron-overload-disease penetrance; Savatt 2023 quantified 34.1%
    // laboratory-overload prevalence in C282Y/C282Y women (median age 62)
    // vs 2.1% in non-homozygotes. Together they justify treating the
    // overload-trigger threshold as a high-priority routing flag.
    supportingCitations: ['allen_2008', 'savatt_2023'],
    manifestRefs: ['I3', 'A10'],
  },
  very_high_ferritin_any_stage: {
    laypersonName: 'Very high ferritin — evaluation needed',
    technicalName: 'Ferritin > 1000 μg/L',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['waalen_2008', 'adams_2005'],
    // Allen 2008 anchors the > 1000 μg/L threshold for symptomatic
    // hemochromatosis disease — the cohort study that established
    // ferritin > 1000 as the evaluation cutoff in C282Y homozygotes.
    supportingCitations: ['cullis_2018', 'goubran_2024', 'allen_2008'],
    manifestRefs: ['A10'],
  },
  microcytic_normal_iron_studies: {
    laypersonName: 'Small red blood cells with adequate iron — thalassemia evaluation recommended',
    technicalName: 'Microcytic anemia with normal or elevated iron studies',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['cappellini_2020', 'van_zeben_1990', 'hans_2024'],
    // Weli 2024 — formal evaluation of 13 discriminant formulas (Mentzer
    // index, Green & King, Ehsani, etc.) for distinguishing β-thalassemia
    // trait from iron deficiency in microcytic anemia. Anchors the
    // pattern's clinical pathway claim that hemoglobin electrophoresis
    // (and not just iron studies) is the disambiguating next test.
    supportingCitations: ['mondal_2021', 'thambiraj_2023', 'mettananda_2020', 'weli_2024'],
    manifestRefs: ['I4'],
  },
  macrocytic_anemia_signal: {
    laypersonName: 'Larger red blood cells — B12 or folate evaluation recommended',
    technicalName: 'Macrocytic anemia signal',
    strength: 'HIGH_QUALITY',
    // Out of FF's primary iron scope — routes to clinician for B12/folate
    // workup. Cited under the broader anemia-differential framework (Skikne
    // 2011) since FF's reference corpus doesn't carry B12/folate-specific
    // primary literature. Manifest section I4 (morphology differential) is
    // the closest fit; the macrocytic case is the "MCV high" arm of the
    // morphology routing tree.
    primaryCitations: ['skikne_2011'],
    supportingCitations: [],
    manifestRefs: ['I4'],
  },
  anemia_with_replete_ferritin: {
    laypersonName: 'Anemia present, but iron stores look adequate',
    technicalName: 'Anemia with non-deficient ferritin, undifferentiated',
    strength: 'HIGH_QUALITY',
    // Anemia signal rests on the WHO-consensus hemoglobin threshold; ferritin
    // boundary rests on the Mei 2024 IPD methodology. The "ferritin doesn't
    // explain the anemia → MCV-based subtype differentiation" routing is
    // consensus clinical hematology (no single primary trial — drawn from
    // Skikne 2011 + Cappellini 2020 + the broader anemia-differential
    // literature already cited for ACD and morphology patterns).
    primaryCitations: ['mei_2024', 'skikne_2011'],
    supportingCitations: ['addo_2025', 'cappellini_2020'],
    manifestRefs: ['I1', 'I4'],
  },
  pregnancy_t1_at_risk_for_t3_deficiency: {
    laypersonName: 'Iron stores in the at-risk band for late-pregnancy deficiency',
    technicalName: 'First-trimester ferritin in the at-risk-for-third-trimester-deficiency band',
    strength: 'PRELIMINARY',
    primaryCitations: ['mccarthy_2024'],
    supportingCitations: ['mei_2024', 'bothwell_2000'],
    manifestRefs: ['A4'],
  },
  mixed_picture_multiple_patterns: {
    laypersonName: 'Your results could fit more than one pattern',
    technicalName: 'Multiple patterns with similar likelihood',
    strength: 'HIGH_QUALITY',
    primaryCitations: [],
    supportingCitations: [],
    manifestRefs: [],
  },
  insufficient_data: {
    laypersonName: 'Not enough information to classify',
    technicalName: 'Inputs insufficient for confident pattern classification',
    strength: 'PRELIMINARY',
    primaryCitations: [],
    supportingCitations: [],
    manifestRefs: [],
  },
  no_established_pattern: {
    laypersonName: 'Your combination doesn\'t match an established research pattern',
    technicalName: 'Full panel provided; no published pattern matches this specific combination',
    strength: 'PRELIMINARY',
    primaryCitations: [],
    supportingCitations: [],
    manifestRefs: [],
  },
  severity_urgent_override: {
    laypersonName: 'Please see a clinician today',
    technicalName: 'Severity override — urgent clinical evaluation recommended',
    strength: 'HIGH_QUALITY',
    primaryCitations: ['mansukhani_2023', 'butwick_2016', 'waalen_2008'],
    supportingCitations: [],
    manifestRefs: ['B7', 'A10'],
  },
};
